import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@monorepo/shared-auth/firebaseConfig';
import { LEAGUE_STATUS, LEAGUE_ROLES } from '../data/leagueSports';

// Create a new league
export const createLeague = async (userId, leagueData) => {
  try {
    const leagueRef = collection(db, 'leagues');
    const newLeague = await addDoc(leagueRef, {
      ...leagueData,
      createdBy: userId,
      status: LEAGUE_STATUS.DRAFT,
      isFilled: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Get creator's user data from users collection
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : {};

    // Add creator as admin with their user data
    await setDoc(doc(db, 'leagues', newLeague.id, 'members', userId), {
      role: LEAGUE_ROLES.ADMIN,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      joinedAt: serverTimestamp(),
      status: 'active'
    });

    return { id: newLeague.id, success: true };
  } catch (error) {
    console.error('Error creating league:', error);
    throw error;
  }
};

// Get league by ID
export const getLeague = async (leagueId) => {
  try {
    const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
    if (leagueDoc.exists()) {
      return { id: leagueDoc.id, ...leagueDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting league:', error);
    throw error;
  }
};

// Get all leagues (with optional filtering)
export const getLeagues = async (filters = {}) => {
  try {
    let q = collection(db, 'leagues');

    const constraints = [];

    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters.sport) {
      constraints.push(where('sport', '==', filters.sport));
    }

    if (filters.visibility) {
      constraints.push(where('visibility', '==', filters.visibility));
    }

    constraints.push(orderBy('createdAt', 'desc'));

    if (filters.limit) {
      constraints.push(limit(filters.limit));
    }

    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }

    const snapshot = await getDocs(q);

    // Fetch member count for each league
    const leaguesWithMemberCount = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const membersSnapshot = await getDocs(
          collection(db, 'leagues', doc.id, 'members')
        );
        return {
          id: doc.id,
          ...doc.data(),
          memberCount: membersSnapshot.size
        };
      })
    );

    return leaguesWithMemberCount;
  } catch (error) {
    console.error('Error getting leagues:', error);
    throw error;
  }
};

// Get leagues where user is a member
export const getUserLeagues = async (userId) => {
  try {
    const leaguesSnapshot = await getDocs(collection(db, 'leagues'));
    const userLeagues = [];

    for (const leagueDoc of leaguesSnapshot.docs) {
      const memberDoc = await getDoc(
        doc(db, 'leagues', leagueDoc.id, 'members', userId)
      );

      if (memberDoc.exists()) {
        // Get member count for this league
        const membersSnapshot = await getDocs(
          collection(db, 'leagues', leagueDoc.id, 'members')
        );

        userLeagues.push({
          id: leagueDoc.id,
          ...leagueDoc.data(),
          userRole: memberDoc.data().role,
          memberCount: membersSnapshot.size
        });
      }
    }

    return userLeagues;
  } catch (error) {
    console.error('Error getting user leagues:', error);
    throw error;
  }
};

// Update league
export const updateLeague = async (leagueId, updates) => {
  try {
    await updateDoc(doc(db, 'leagues', leagueId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating league:', error);
    throw error;
  }
};

// Delete league
export const deleteLeague = async (leagueId) => {
  try {
    await deleteDoc(doc(db, 'leagues', leagueId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting league:', error);
    throw error;
  }
};

// Archive league (pauses billing, preserves data)
export const archiveLeague = async (leagueId) => {
  try {
    // Store the previous status before archiving
    const league = await getLeague(leagueId);
    await updateDoc(doc(db, 'leagues', leagueId), {
      status: LEAGUE_STATUS.ARCHIVED,
      previousStatus: league.status, // Store for reactivation
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error archiving league:', error);
    throw error;
  }
};

// Reactivate archived league (resumes billing)
export const reactivateLeague = async (leagueId) => {
  try {
    const league = await getLeague(leagueId);
    // Restore to previous status or default to 'in_progress'
    const newStatus = league.previousStatus || LEAGUE_STATUS.IN_PROGRESS;
    await updateDoc(doc(db, 'leagues', leagueId), {
      status: newStatus,
      previousStatus: null,
      reactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error reactivating league:', error);
    throw error;
  }
};

// Check if league is archived
export const isLeagueArchived = async (leagueId) => {
  try {
    const league = await getLeague(leagueId);
    return league?.status === LEAGUE_STATUS.ARCHIVED;
  } catch (error) {
    console.error('Error checking archive status:', error);
    return false;
  }
};

// League Members Management

// Helper function to check and update league filled status
const checkAndUpdateFilledStatus = async (leagueId) => {
  try {
    const [leagueData, membersData] = await Promise.all([
      getLeague(leagueId),
      getLeagueMembers(leagueId)
    ]);

    if (leagueData && leagueData.maxParticipants) {
      const isFilled = membersData.length >= leagueData.maxParticipants;
      await updateDoc(doc(db, 'leagues', leagueId), {
        isFilled,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating filled status:', error);
    // Don't throw - this is a secondary operation
  }
};

// Add member to league
export const addLeagueMember = async (leagueId, userId, memberData) => {
  try {
    await setDoc(doc(db, 'leagues', leagueId, 'members', userId), {
      ...memberData,
      role: memberData.role || LEAGUE_ROLES.PLAYER,
      teamName: memberData.teamName || null,
      teamCaptainId: memberData.teamCaptainId || null,
      joinedAt: serverTimestamp(),
      status: 'active'
    });

    // Check and update filled status
    await checkAndUpdateFilledStatus(leagueId);

    return { success: true };
  } catch (error) {
    console.error('Error adding league member:', error);
    throw error;
  }
};

// Get league members
export const getLeagueMembers = async (leagueId) => {
  try {
    const membersSnapshot = await getDocs(
      collection(db, 'leagues', leagueId, 'members')
    );
    return membersSnapshot.docs.map(doc => ({ userId: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting league members:', error);
    throw error;
  }
};

// Update member role
export const updateMemberRole = async (leagueId, userId, role) => {
  try {
    await updateDoc(doc(db, 'leagues', leagueId, 'members', userId), {
      role
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

// Remove member from league
export const removeLeagueMember = async (leagueId, userId) => {
  try {
    await deleteDoc(doc(db, 'leagues', leagueId, 'members', userId));

    // Check and update filled status
    await checkAndUpdateFilledStatus(leagueId);

    return { success: true };
  } catch (error) {
    console.error('Error removing league member:', error);
    throw error;
  }
};

// Check if user is admin
export const isLeagueAdmin = async (leagueId, userId) => {
  try {
    const memberDoc = await getDoc(
      doc(db, 'leagues', leagueId, 'members', userId)
    );

    if (memberDoc.exists()) {
      return memberDoc.data().role === LEAGUE_ROLES.ADMIN;
    }
    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Match Management

// Create match
export const createMatch = async (leagueId, matchData) => {
  try {
    const matchRef = collection(db, 'leagues', leagueId, 'matches');
    const newMatch = await addDoc(matchRef, {
      ...matchData,
      createdAt: serverTimestamp()
    });
    return { id: newMatch.id, success: true };
  } catch (error) {
    console.error('Error creating match:', error);
    throw error;
  }
};

// Get league matches
export const getLeagueMatches = async (leagueId) => {
  try {
    const matchesSnapshot = await getDocs(
      query(
        collection(db, 'leagues', leagueId, 'matches'),
        orderBy('round', 'asc')
      )
    );
    return matchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting league matches:', error);
    throw error;
  }
};

// Update match (primarily for score entry)
export const updateMatch = async (leagueId, matchId, updates) => {
  try {
    await updateDoc(doc(db, 'leagues', leagueId, 'matches', matchId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating match:', error);
    throw error;
  }
};

// Update match score
export const updateMatchScore = async (leagueId, matchId, scoreData) => {
  try {
    await updateDoc(doc(db, 'leagues', leagueId, 'matches', matchId), {
      participant1Score: scoreData.participant1Score,
      participant2Score: scoreData.participant2Score,
      winnerId: scoreData.winnerId,
      status: scoreData.status,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating match score:', error);
    throw error;
  }
};

// Standings Management

// Update standings for a participant
export const updateStandings = async (leagueId, participantId, standingsData) => {
  try {
    await setDoc(
      doc(db, 'leagues', leagueId, 'standings', participantId),
      {
        ...standingsData,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
    return { success: true };
  } catch (error) {
    console.error('Error updating standings:', error);
    throw error;
  }
};

// Get league standings
export const getLeagueStandings = async (leagueId) => {
  try {
    const standingsSnapshot = await getDocs(
      query(
        collection(db, 'leagues', leagueId, 'standings'),
        orderBy('rank', 'asc')
      )
    );
    return standingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting league standings:', error);
    throw error;
  }
};

// Join Requests Management

// Submit join request
export const submitJoinRequest = async (leagueId, userId, userData) => {
  try {
    await setDoc(doc(db, 'leagues', leagueId, 'joinRequests', userId), {
      userId,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      status: 'pending',
      requestedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error submitting join request:', error);
    throw error;
  }
};

// Get league join requests
export const getJoinRequests = async (leagueId) => {
  try {
    const requestsSnapshot = await getDocs(
      query(
        collection(db, 'leagues', leagueId, 'joinRequests'),
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc')
      )
    );
    return requestsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting join requests:', error);
    throw error;
  }
};

// Approve join request
export const approveJoinRequest = async (leagueId, userId, userData) => {
  try {
    // Add user as member
    await addLeagueMember(leagueId, userId, {
      role: LEAGUE_ROLES.PLAYER,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email
    });

    // Update request status
    await updateDoc(doc(db, 'leagues', leagueId, 'joinRequests', userId), {
      status: 'approved',
      approvedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving join request:', error);
    throw error;
  }
};

// Deny join request
export const denyJoinRequest = async (leagueId, userId) => {
  try {
    await updateDoc(doc(db, 'leagues', leagueId, 'joinRequests', userId), {
      status: 'denied',
      deniedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error denying join request:', error);
    throw error;
  }
};

// Check if user has pending request
export const hasPendingRequest = async (leagueId, userId) => {
  try {
    const requestDoc = await getDoc(
      doc(db, 'leagues', leagueId, 'joinRequests', userId)
    );
    return requestDoc.exists() && requestDoc.data().status === 'pending';
  } catch (error) {
    console.error('Error checking pending request:', error);
    return false;
  }
};

// Message Board

// Add message to league
export const addLeagueMessage = async (leagueId, userId, userName, message) => {
  try {
    const messageRef = collection(db, 'leagues', leagueId, 'messages');
    await addDoc(messageRef, {
      userId,
      userName,
      message,
      timestamp: serverTimestamp(),
      edited: false
    });
    return { success: true };
  } catch (error) {
    console.error('Error adding league message:', error);
    throw error;
  }
};

// Get league messages
export const getLeagueMessages = async (leagueId, limitCount = 50) => {
  try {
    const messagesSnapshot = await getDocs(
      query(
        collection(db, 'leagues', leagueId, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      )
    );
    return messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting league messages:', error);
    throw error;
  }
};

// Team Management (for team-based leagues)

// Get teams for a league (grouping members by team)
export const getLeagueTeams = async (leagueId) => {
  try {
    const members = await getLeagueMembers(leagueId);

    // Group members by team
    const teams = {};
    members.forEach(member => {
      if (member.teamName) {
        if (!teams[member.teamName]) {
          teams[member.teamName] = {
            teamName: member.teamName,
            captain: null,
            players: []
          };
        }

        if (member.role === LEAGUE_ROLES.CAPTAIN) {
          teams[member.teamName].captain = member;
        } else {
          teams[member.teamName].players.push(member);
        }
      }
    });

    return Object.values(teams);
  } catch (error) {
    console.error('Error getting league teams:', error);
    throw error;
  }
};

// Get members of a specific team
export const getTeamMembers = async (leagueId, teamName) => {
  try {
    const members = await getLeagueMembers(leagueId);
    return members.filter(m => m.teamName === teamName);
  } catch (error) {
    console.error('Error getting team members:', error);
    throw error;
  }
};

// Check if user is team captain
export const isTeamCaptain = async (leagueId, userId) => {
  try {
    const memberDoc = await getDoc(
      doc(db, 'leagues', leagueId, 'members', userId)
    );

    if (memberDoc.exists()) {
      return memberDoc.data().role === LEAGUE_ROLES.CAPTAIN;
    }
    return false;
  } catch (error) {
    console.error('Error checking captain status:', error);
    return false;
  }
};

// Get captain's team name
export const getCaptainTeam = async (leagueId, userId) => {
  try {
    const memberDoc = await getDoc(
      doc(db, 'leagues', leagueId, 'members', userId)
    );

    if (memberDoc.exists() && memberDoc.data().role === LEAGUE_ROLES.CAPTAIN) {
      return memberDoc.data().teamName;
    }
    return null;
  } catch (error) {
    console.error('Error getting captain team:', error);
    return null;
  }
};

// Gallery Management

// Add photo to league gallery
export const addGalleryPhoto = async (leagueId, photoData) => {
  try {
    const photoRef = collection(db, 'leagues', leagueId, 'gallery');
    const newPhoto = await addDoc(photoRef, {
      ...photoData,
      uploadedAt: serverTimestamp()
    });
    return { id: newPhoto.id, success: true };
  } catch (error) {
    console.error('Error adding gallery photo:', error);
    throw error;
  }
};

// Get league gallery photos
export const getGalleryPhotos = async (leagueId, limitCount = 50) => {
  try {
    const photosSnapshot = await getDocs(
      query(
        collection(db, 'leagues', leagueId, 'gallery'),
        orderBy('uploadedAt', 'desc'),
        limit(limitCount)
      )
    );
    return photosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting gallery photos:', error);
    throw error;
  }
};

// Delete gallery photo
export const deleteGalleryPhoto = async (leagueId, photoId) => {
  try {
    await deleteDoc(doc(db, 'leagues', leagueId, 'gallery', photoId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting gallery photo:', error);
    throw error;
  }
};

export default {
  createLeague,
  getLeague,
  getLeagues,
  getUserLeagues,
  updateLeague,
  deleteLeague,
  archiveLeague,
  reactivateLeague,
  isLeagueArchived,
  addLeagueMember,
  getLeagueMembers,
  updateMemberRole,
  removeLeagueMember,
  isLeagueAdmin,
  submitJoinRequest,
  getJoinRequests,
  approveJoinRequest,
  denyJoinRequest,
  hasPendingRequest,
  createMatch,
  getLeagueMatches,
  updateMatch,
  updateMatchScore,
  updateStandings,
  getLeagueStandings,
  addLeagueMessage,
  getLeagueMessages,
  getLeagueTeams,
  getTeamMembers,
  isTeamCaptain,
  getCaptainTeam,
  addGalleryPhoto,
  getGalleryPhotos,
  deleteGalleryPhoto
};
